import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Input,
  Form,
  Row,
  Col,
  message,
  Breadcrumb,
  Select,
} from "antd";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import WelcomeTopbar from "../../components/common/header/WelcomeTopbar";
import { updateSettingsRequest } from "../../api/requests/setting";
import { useSettings } from "../../api/hooks";
import { useCurrentUser } from "../../api/hooks/auth";
import {
  BIN_WIEGHT,
  BINS_COUNT_PER_PALLET,
  PALLET_WIEGHT,
} from "../../constants";

const updateSettingsSchemaResolver = yupResolver(
  yup.object().shape({
    emails: yup.object().shape({
      senderName: yup.string().required("Sender name is required"),
      replyTo: yup
        .array()
        .of(yup.string().email("Must be a valid email"))
        .min(1, "At least one reply-to email is required"),
      cc: yup.array().of(yup.string().email("Must be a valid email")),
    }),
    shipping: yup.object().shape({
      binWeight: yup
        .number()
        .required("Bin weight is required")
        .positive("Bin weight must be positive"),
      binsPerPallet: yup
        .number()
        .required("Bins per pallet is required")
        .positive("Bins per pallet must be positive")
        .integer("Bins per pallet must be an integer"),
      palletWeight: yup
        .number()
        .required("Pallet weight is required")
        .positive("Pallet weight must be positive"),
    }),
    warehouses: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required("Warehouse name is required"),
          address: yup.object().shape({
            street: yup.string().required("Street is required"),
            city: yup.string().required("City is required"),
            state: yup.string().required("State is required"),
            zipCode: yup.string().required("Zip code is required"),
            country: yup.string().required("Country is required"),
          }),
        }),
      )
      .min(1, "At least one warehouse is required"),
  }),
);

const initialWarehouse = {
  name: "",
  address: {
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  },
};

function UpdateSettingsPage() {
  const queryClient = useQueryClient();
  const { data: userData } = useCurrentUser();
  // for all the users other than the usersname "system" all the fields and action buttons need to be disabled on this screen.
  const isSystemUser = userData?.systemUser?.username === "system";
  // console.log("isSystemUser----->", isSystemUser);

  const { data: settingDetails } = useSettings();

  const { mutateAsync: updateSettings, isPending: updatingSettings } =
    useMutation({
      mutationFn: async (data) => {
        const res = await updateSettingsRequest({ data });
        return res.data;
      },
      mutationKey: [`/v1/settings`],
      onSuccess: (res) => {
        queryClient.invalidateQueries([`/v1/settings`]);
        const successMessage = res?.message;
        if (successMessage) {
          message.success(successMessage);
        }
      },
      onError: (error) => {
        mutationOnErrorHandler({ error, message });
      },
    });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: updateSettingsSchemaResolver,
  });

  const {
    fields: warehouseFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "warehouses",
  });

  // console.log("errors----->", errors);
  // console.log("getValues----->", getValues());

  async function onSubmit(data) {
    if (!isSystemUser) {
      console.log("isSystemUser----->", isSystemUser);
      return;
    }
    // console.log(data);
    const res = await updateSettings(data);
    // console.log("res----->", res);
  }

  useEffect(() => {
    if (settingDetails) {
      const { shipping = {} } = settingDetails;
      const {
        binWeight = BIN_WIEGHT,
        binsPerPallet = BINS_COUNT_PER_PALLET,
        palletWeight = PALLET_WIEGHT,
      } = shipping;
      reset({
        emails: {
          senderName: settingDetails.emails?.senderName || "",
          replyTo: settingDetails.emails?.replyTo || [],
          cc: settingDetails.emails?.cc || [],
        },
        shipping: {
          binWeight: binWeight,
          binsPerPallet: binsPerPallet,
          palletWeight: palletWeight,
        },
        warehouses: settingDetails.warehouses || [],
      });
    }
  }, [settingDetails, reset]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-5 pb-6">
        <div className="flex flex-col gap-6">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
              <Breadcrumb
                items={[
                  {
                    title: <div className="text-lg">Settings</div>,
                  },
                ]}
              />
            </div>
            <div className="px-6 py-6">
              <Row gutter={18}>
                {/* Email Settings */}
                <Col span={24}>
                  <p className="mb-4 text-lg">Email Settings</p>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Sender Name"
                    validateStatus={errors.emails?.senderName ? "error" : ""}
                    help={errors.emails?.senderName?.message}
                    required
                  >
                    <Controller
                      control={control}
                      name="emails.senderName"
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Sender Name"
                          disabled={!isSystemUser}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Reply-To Emails"
                    validateStatus={errors.emails?.replyTo ? "error" : ""}
                    help={errors.emails?.replyTo?.message}
                    required
                  >
                    <Controller
                      control={control}
                      name="emails.replyTo"
                      render={({ field }) => (
                        <Select
                          mode="tags"
                          {...field}
                          placeholder="Enter email addresses (press Enter to add)"
                          tokenSeparators={[",", " "]}
                          // style={{ width: "100%" }}
                          disabled={!isSystemUser}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="CC Emails"
                    validateStatus={errors.emails?.cc ? "error" : ""}
                    help={errors.emails?.cc?.message}
                  >
                    <Controller
                      control={control}
                      name="emails.cc"
                      render={({ field }) => (
                        <Select
                          mode="tags"
                          {...field}
                          placeholder="Enter email addresses (press Enter to add)"
                          tokenSeparators={[",", " "]}
                          // style={{ width: "100%" }}
                          disabled={!isSystemUser}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>

                {/* Shipping Settings */}
                <Col span={24}>
                  <p className="mb-4 text-lg">Shipping Settings</p>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Bin Weight (lbs)"
                    validateStatus={errors.shipping?.binWeight ? "error" : ""}
                    help={errors.shipping?.binWeight?.message}
                    required
                  >
                    <Controller
                      control={control}
                      name="shipping.binWeight"
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          step={0.1}
                          placeholder="Bin Weight"
                          disabled={!isSystemUser}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Bins Per Pallet"
                    validateStatus={
                      errors.shipping?.binsPerPallet ? "error" : ""
                    }
                    help={errors.shipping?.binsPerPallet?.message}
                    required
                  >
                    <Controller
                      control={control}
                      name="shipping.binsPerPallet"
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min={1}
                          step={1}
                          placeholder="Bins Per Pallet"
                          disabled={!isSystemUser}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Pallet Weight (lbs)"
                    validateStatus={
                      errors.shipping?.palletWeight ? "error" : ""
                    }
                    help={errors.shipping?.palletWeight?.message}
                    required
                  >
                    <Controller
                      control={control}
                      name="shipping.palletWeight"
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          min={0}
                          step={0.1}
                          placeholder="Pallet Weight"
                          disabled={!isSystemUser}
                        />
                      )}
                    />
                  </Form.Item>
                </Col>

                {/* Warehouses */}
                <Col span={24} className="mt-6 flex justify-between">
                  <p className="text-lg">Warehouses</p>
                  <Button
                    htmlType="button"
                    onClick={() => append(initialWarehouse)}
                    disabled={!isSystemUser}
                  >
                    <PlusCircleOutlined /> Add Warehouse
                  </Button>
                </Col>

                {warehouseFields.map((field, index) => (
                  <Col span={24} key={field.id} className="mb-6">
                    <Row gutter={18}>
                      <Col span={24}>
                        <Form.Item
                          label="Warehouse Name"
                          validateStatus={
                            errors.warehouses?.[index]?.name ? "error" : ""
                          }
                          help={errors.warehouses?.[index]?.name?.message}
                          required
                        >
                          <Controller
                            control={control}
                            name={`warehouses.${index}.name`}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="Warehouse Name"
                                disabled={!isSystemUser}
                              />
                            )}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="Street"
                          validateStatus={
                            errors.warehouses?.[index]?.address?.street
                              ? "error"
                              : ""
                          }
                          help={
                            errors.warehouses?.[index]?.address?.street?.message
                          }
                          required
                        >
                          <Controller
                            control={control}
                            name={`warehouses.${index}.address.street`}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="Street"
                                disabled={!isSystemUser}
                              />
                            )}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="City"
                          validateStatus={
                            errors.warehouses?.[index]?.address?.city
                              ? "error"
                              : ""
                          }
                          help={
                            errors.warehouses?.[index]?.address?.city?.message
                          }
                          required
                        >
                          <Controller
                            control={control}
                            name={`warehouses.${index}.address.city`}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="City"
                                disabled={!isSystemUser}
                              />
                            )}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="State"
                          validateStatus={
                            errors.warehouses?.[index]?.address?.state
                              ? "error"
                              : ""
                          }
                          help={
                            errors.warehouses?.[index]?.address?.state?.message
                          }
                          required
                        >
                          <Controller
                            control={control}
                            name={`warehouses.${index}.address.state`}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="State"
                                disabled={!isSystemUser}
                              />
                            )}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="Zip Code"
                          validateStatus={
                            errors.warehouses?.[index]?.address?.zipCode
                              ? "error"
                              : ""
                          }
                          help={
                            errors.warehouses?.[index]?.address?.zipCode
                              ?.message
                          }
                          required
                        >
                          <Controller
                            control={control}
                            name={`warehouses.${index}.address.zipCode`}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="Zip Code"
                                disabled={!isSystemUser}
                              />
                            )}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="Country"
                          validateStatus={
                            errors.warehouses?.[index]?.address?.country
                              ? "error"
                              : ""
                          }
                          help={
                            errors.warehouses?.[index]?.address?.country
                              ?.message
                          }
                          required
                        >
                          <Controller
                            control={control}
                            name={`warehouses.${index}.address.country`}
                            render={({ field }) => (
                              <Input
                                {...field}
                                placeholder="Country"
                                disabled={!isSystemUser}
                              />
                            )}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Button
                          onClick={() => remove(index)}
                          className="mb-6"
                          disabled={!isSystemUser}
                        >
                          <DeleteOutlined /> Remove Warehouse
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                ))}

                <Col span={24}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updatingSettings}
                    disabled={!isSystemUser}
                  >
                    Update Settings
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default UpdateSettingsPage;
